import {
  APP_DATA,
  FEATURE_LIST,
  REMOTE_STOP_SCREENSHARE_TYPE,
  SESSION_STORE_KEY,
} from "../common/constants";
import { Box, Flex, Slider, StyledMenuTile, Text } from "@100mslive/react-ui";
import {
  HorizontalMenuIcon,
  MicOffIcon,
  MicOnIcon,
  PinIcon,
  RemoveUserIcon,
  ShareScreenIcon,
  SpeakerIcon,
  StarIcon,
  VideoOffIcon,
  VideoOnIcon,
} from "@100mslive/react-icons";
import React, { Fragment, useState } from "react";
import {
  selectLocalPeerID,
  selectPermissions,
  selectSessionStore,
  selectTemplateAppData,
  selectTrackByID,
  selectVideoTrackByPeerID,
  useCustomEvent,
  useHMSActions,
  useHMSStore,
  useRemoteAVToggle,
} from "@100mslive/react-sdk";

import { ToastManager } from "./Toast/ToastManager";
import { convertResolutionName } from "../common/utils";
import { useDropdownList } from "./hooks/useDropdownList";
import { useDropdownSelection } from "./hooks/useDropdownSelection";
import { useIsFeatureEnabled } from "./hooks/useFeatures";
import { useSetAppDataByKey } from "./AppData/useUISettings";
import { useTranslation } from "react-i18next";

const isSameTile = ({ trackId, videoTrackID, audioTrackID }) =>
  trackId &&
  ((videoTrackID && videoTrackID === trackId) ||
    (audioTrackID && audioTrackID === trackId));

const SpotlightActions = ({ audioTrackID, videoTrackID }) => {
  const hmsActions = useHMSActions();
  const spotlightTrackId = useHMSStore(
    selectSessionStore(SESSION_STORE_KEY.SPOTLIGHT)
  );
  const { t } = useTranslation();

  const isTileSpotlighted = isSameTile({
    trackId: spotlightTrackId,
    videoTrackID,
    audioTrackID,
  });

  const setSpotlightTrackId = trackId =>
    hmsActions.sessionStore
      .set(SESSION_STORE_KEY.SPOTLIGHT, trackId)
      .catch(err => ToastManager.addToast({ title: err.description }));

  return (
    <StyledMenuTile.ItemButton
      onClick={() =>
        isTileSpotlighted
          ? setSpotlightTrackId()
          : setSpotlightTrackId(videoTrackID || audioTrackID)
      }
    >
      <StarIcon />
      <span>
        {isTileSpotlighted
          ? t("lbl_removed_from_prominent_position")
          : t("lbl_outstanding_position_for_everyone")}
      </span>
    </StyledMenuTile.ItemButton>
  );
};

const PinActions = ({ audioTrackID, videoTrackID }) => {
  const [pinnedTrackId, setPinnedTrackId] = useSetAppDataByKey(
    APP_DATA.pinnedTrackId
  );
  const { t } = useTranslation();

  const isTilePinned = isSameTile({
    trackId: pinnedTrackId,
    videoTrackID,
    audioTrackID,
  });

  return (
    <>
      <StyledMenuTile.ItemButton
        onClick={() =>
          isTilePinned
            ? setPinnedTrackId()
            : setPinnedTrackId(videoTrackID || audioTrackID)
        }
      >
        <PinIcon />
        <span>
          {t("fm_pin_screen", {
            action: t(isTilePinned ? "lbl_unpin" : "lbl_pin"),
          })}
        </span>
      </StyledMenuTile.ItemButton>
    </>
  );
};

/**
 * Taking peerID as peer won't necesarilly have tracks
 */
const TileMenu = ({
  audioTrackID,
  videoTrackID,
  peerID,
  isScreenshare = false,
}) => {
  const [open, setOpen] = useState(false);
  const actions = useHMSActions();
  const localPeerID = useHMSStore(selectLocalPeerID);
  const isLocal = localPeerID === peerID;
  const { removeOthers, changeRole } = useHMSStore(selectPermissions);
  const showSpotlight = changeRole;

  const {
    isAudioEnabled,
    isVideoEnabled,
    setVolume,
    toggleAudio,
    toggleVideo,
    volume,
  } = useRemoteAVToggle(audioTrackID, videoTrackID);
  const { sendEvent } = useCustomEvent({
    type: REMOTE_STOP_SCREENSHARE_TYPE,
  });
  const { t } = useTranslation();

  const isPrimaryVideoTrack =
    useHMSStore(selectVideoTrackByPeerID(peerID))?.id === videoTrackID;
  const uiMode = useHMSStore(selectTemplateAppData).uiMode;
  const isInset = uiMode === "inset";

  const isPinEnabled = useIsFeatureEnabled(FEATURE_LIST.PIN_TILE);
  const showPinAction =
    isPinEnabled &&
    (audioTrackID || (videoTrackID && isPrimaryVideoTrack)) &&
    !isInset;

  const track = useHMSStore(selectTrackByID(videoTrackID));
  const hideSimulcastLayers =
    !track?.layerDefinitions?.length || track.degraded || !track.enabled;

  useDropdownList({ open, name: "TileMenu" });

  if (
    !(
      removeOthers ||
      toggleAudio ||
      toggleVideo ||
      setVolume ||
      showPinAction
    ) &&
    hideSimulcastLayers
  ) {
    return null;
  }

  if (isInset && isLocal) {
    return null;
  }

  return (
    <StyledMenuTile.Root
      css={{
        width: "fit-content",
      }}
      open={open}
      onOpenChange={setOpen}
    >
      <StyledMenuTile.Trigger
        data-testid="participant_menu_btn"
        onClick={e => e.stopPropagation()}
      >
        <HorizontalMenuIcon />
      </StyledMenuTile.Trigger>
      <StyledMenuTile.Content side="top" align="end">
        {isLocal ? (
          showPinAction && (
            <>
              <PinActions
                audioTrackID={audioTrackID}
                videoTrackID={videoTrackID}
              />
              {showSpotlight && (
                <SpotlightActions
                  audioTrackID={audioTrackID}
                  videoTrackID={videoTrackID}
                />
              )}
            </>
          )
        ) : (
          <>
            {toggleVideo ? (
              <StyledMenuTile.ItemButton
                onClick={toggleVideo}
                data-testid={
                  isVideoEnabled
                    ? "mute_video_participant_btn"
                    : "unmute_video_participant_btn"
                }
              >
                {isVideoEnabled ? <VideoOnIcon /> : <VideoOffIcon />}
                <span>{`${
                  isVideoEnabled ? "Tắt camera" : "Gửi yêu cầu bật camera"
                }`}</span>
              </StyledMenuTile.ItemButton>
            ) : null}
            {toggleAudio ? (
              <StyledMenuTile.ItemButton
                onClick={toggleAudio}
                data-testid={
                  isVideoEnabled
                    ? "mute_audio_participant_btn"
                    : "unmute_audio_participant_btn"
                }
              >
                {isAudioEnabled ? <MicOnIcon /> : <MicOffIcon />}
                <span>{`${
                  isAudioEnabled ? "Tắt tiếng" : "Gửi yêu cầu bật tiếng"
                }`}</span>
              </StyledMenuTile.ItemButton>
            ) : null}
            {audioTrackID ? (
              <StyledMenuTile.VolumeItem data-testid="participant_volume_slider">
                <Flex align="center" gap={1}>
                  <SpeakerIcon />
                  <Box as="span" css={{ ml: "$4" }}>
                    {t("fm_volume", { number: volume })}
                  </Box>
                </Flex>
                <Slider
                  css={{ my: "0.5rem" }}
                  step={5}
                  value={[volume]}
                  onValueChange={e => setVolume(e[0])}
                />
              </StyledMenuTile.VolumeItem>
            ) : null}
            {showPinAction && (
              <>
                <PinActions
                  audioTrackID={audioTrackID}
                  videoTrackID={videoTrackID}
                />
                {showSpotlight && (
                  <SpotlightActions
                    audioTrackID={audioTrackID}
                    videoTrackID={videoTrackID}
                  />
                )}
              </>
            )}
            <SimulcastLayers trackId={videoTrackID} />
            {removeOthers ? (
              <StyledMenuTile.RemoveItem
                onClick={async () => {
                  try {
                    await actions.removePeer(peerID, "");
                  } catch (error) {
                    // TODO: Toast here
                  }
                }}
                data-testid="remove_participant_btn"
              >
                <RemoveUserIcon />
                <span>Bỏ người tham gia</span>
              </StyledMenuTile.RemoveItem>
            ) : null}

            {removeOthers && isScreenshare ? (
              <StyledMenuTile.RemoveItem onClick={() => sendEvent({})}>
                <ShareScreenIcon />
                <span>Dừng chia sẻ màn hình</span>
              </StyledMenuTile.RemoveItem>
            ) : null}
          </>
        )}
      </StyledMenuTile.Content>
    </StyledMenuTile.Root>
  );
};

const SimulcastLayers = ({ trackId }) => {
  const { t } = useTranslation();
  const track = useHMSStore(selectTrackByID(trackId));
  const actions = useHMSActions();
  const bg = useDropdownSelection();
  if (!track?.layerDefinitions?.length || track.degraded || !track.enabled) {
    return null;
  }
  const currentLayer = track.layerDefinitions.find(
    layer => layer.layer === track.layer
  );
  return (
    <Fragment>
      <StyledMenuTile.ItemButton
        css={{ color: "$textMedEmp", cursor: "default" }}
      >
        {t("lbl_select_maximum_resolution")}
      </StyledMenuTile.ItemButton>
      {track.layerDefinitions.map(layer => {
        return (
          <StyledMenuTile.ItemButton
            key={layer.layer}
            onClick={async () => {
              await actions.setPreferredLayer(trackId, layer.layer);
            }}
            css={{
              justifyContent: "space-between",
              bg: track.preferredLayer === layer.layer ? bg : undefined,
              "&:hover": {
                bg: track.preferredLayer === layer.layer ? bg : undefined,
              },
            }}
          >
            <Text
              as="span"
              css={{
                textTransform: "capitalize",
                mr: "$2",
                fontWeight:
                  track.preferredLayer === layer.layer
                    ? "$semiBold"
                    : "$regular",
              }}
            >
              {convertResolutionName(layer.layer, t)}
            </Text>
            <Text as="span" variant="xs" css={{ color: "$textMedEmp" }}>
              {layer.resolution.width}x{layer.resolution.height}
            </Text>
          </StyledMenuTile.ItemButton>
        );
      })}
      <StyledMenuTile.ItemButton>
        <Text as="span" variant="xs" css={{ color: "$textMedEmp" }}>
          {t("lbl_currently_streaming")}
          <Text
            as="span"
            variant="xs"
            css={{
              fontWeight: "$semiBold",
              textTransform: "capitalize",
              color: "$textMedEmp",
              ml: "$2",
            }}
          >
            {currentLayer ? (
              <>
                {(convertResolutionName(track.layer), t)} (
                {currentLayer.resolution.width}x{currentLayer.resolution.height}
                )
              </>
            ) : (
              "-"
            )}
          </Text>
        </Text>
      </StyledMenuTile.ItemButton>
    </Fragment>
  );
};

export default TileMenu;
